import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Radar, Sparkles, Send, Copy, Video, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const FLIERS = ["speed-flier.jpg", "about-poster.jpg", "about-square.jpg", "compare-all.jpg", "compare-fees.jpg", "compare-protection.jpg"];

export const MarketingAutopilotCard = () => {
  const [niche, setNiche] = useState("");
  const [region, setRegion] = useState("Africa and global");
  const [busy, setBusy] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [cfg, setCfg] = useState({ telegram: false, twitter: false });
  const [videos, setVideos] = useState([]);
  const [packBusy, setPackBusy] = useState(null);
  const [postBusy, setPostBusy] = useState(null);
  const [flier, setFlier] = useState(FLIERS[0]);
  const [autopost, setAutopost] = useState({ enabled: false, hour_utc: 8, configured: false });

  const load = useCallback(() => {
    api.get("/marketing/prospects").then(({ data }) => setProspects(data.prospects || [])).catch(() => {});
    api.get("/marketing/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/marketing/videos").then(({ data }) => setVideos(data.videos || [])).catch(() => {});
    api.get("/marketing/autopost").then(({ data }) => setAutopost(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const discover = async () => {
    if (niche.trim().length < 3) return toast.error("Describe who you want to reach");
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/discover", { niche, region, live: true });
      toast.success(`Found ${data.prospects.length} communities 🎯 ${data.source === "live_google_search" ? "(live Google search)" : "(AI knowledge)"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery failed"); }
    finally { setBusy(false); }
  };

  const genPack = async (id) => {
    setPackBusy(id);
    try {
      await api.post(`/marketing/prospects/${id}/pack`, { flier });
      toast.success("Outreach pack ready 📦");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pack failed"); }
    finally { setPackBusy(null); }
  };

  const postTo = async (channel, p) => {
    setPostBusy(p.id + channel);
    try {
      await api.post(`/marketing/post/${channel}`, {
        caption: p.pack.caption, flier: p.pack.flier.replace("/campaign/", ""), prospect_id: p.id,
      });
      toast.success(`Posted to ${channel} ✅`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    finally { setPostBusy(null); }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  const exportCsv = async () => {
    try {
      const res = await api.get("/marketing/prospects.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "vismarks-prospects.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Prospect map downloaded 📊");
    } catch { toast.error("Export failed"); }
  };

  const toggleAutopost = async () => {
    try {
      const { data } = await api.post("/marketing/autopost", { enabled: !autopost.enabled, hour_utc: autopost.hour_utc || 8 });
      setAutopost((s) => ({ ...s, ...data }));
      toast.success(data.enabled ? "Daily auto-post ON ☀️" : "Daily auto-post off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/marketing/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded 🎬");
      load();
    } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  const clusters = [...new Set(prospects.map((p) => p.cluster).filter(Boolean))];

  return (
    <div data-testid="marketing-autopilot-card" className="rounded-2xl border border-neutral-800 bg-neutral-950/80 p-6">
      <div className="flex items-center gap-3">
        <span className="rounded-lg bg-neutral-900 p-2 text-[#007AFF]"><Radar size={18} /></span>
        <div className="font-bold text-sm uppercase tracking-widest">Marketing Autopilot</div>
        <span className="text-[10px] text-neutral-500 border border-[#27272A] rounded-full px-2 py-0.5">
          TG {cfg.telegram ? "✅" : "⛔ add keys"} · X {cfg.twitter ? "✅" : "⛔ add keys"}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={toggleAutopost} data-testid="autopost-toggle"
            className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${autopost.enabled ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : "border-[#27272A] text-neutral-400 hover:text-white"}`}>
            ☀️ Daily auto-post: {autopost.enabled ? `ON · ${String(autopost.hour_utc).padStart(2, "0")}:00 UTC` : "off"}
          </button>
          <button onClick={exportCsv} data-testid="export-csv-btn"
            className="text-[10px] font-bold px-2.5 py-1 rounded-full border border-[#27272A] text-neutral-400 hover:text-white">
            ⬇ Export CSV
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <input value={niche} onChange={(e) => setNiche(e.target.value)} data-testid="autopilot-niche-input"
          placeholder="Who do you want to reach? e.g. fashion sellers in Nigeria & Kenya"
          className="flex-1 min-w-[240px] bg-black/40 border border-[#27272A] rounded-md text-xs px-3 py-2" />
        <input value={region} onChange={(e) => setRegion(e.target.value)} data-testid="autopilot-region-input"
          className="w-44 bg-black/40 border border-[#27272A] rounded-md text-xs px-3 py-2" />
        <select value={flier} onChange={(e) => setFlier(e.target.value)} data-testid="autopilot-flier-select"
          className="bg-black/40 border border-[#27272A] rounded-md text-xs px-2 py-2">
          {FLIERS.map((f) => <option key={f} value={f}>{f.replace(".jpg", "")}</option>)}
        </select>
        <button onClick={discover} disabled={busy} data-testid="autopilot-discover-btn"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-[#007AFF] text-white hover:bg-[#0068d9] disabled:opacity-50">
          <Sparkles className="h-3.5 w-3.5" /> {busy ? "Discovering…" : "Discover communities"}
        </button>
      </div>

      {clusters.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {clusters.map((c) => (
            <span key={c} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/30 text-[#007AFF]">{c}</span>
          ))}
        </div>
      )}

      {prospects.length > 0 && (
        <ul className="mt-4 space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="autopilot-prospects">
          {prospects.map((p) => (
            <li key={p.id} className="rounded-xl border border-[#27272A] bg-black/30 p-3 text-xs" data-testid={`prospect-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-white">{p.name}</span>
                <span className="text-neutral-500">{p.platform}</span>
                <span className="text-emerald-400 font-mono">fit {p.fit_score}/10</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-900 border border-[#27272A] text-neutral-400">{p.cluster}</span>
                <span className={`uppercase text-[9px] font-bold ${p.status === "posted" ? "text-emerald-400" : p.status === "ready" ? "text-[#007AFF]" : "text-neutral-500"}`}>{p.status}</span>
                {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-[#007AFF]"><ExternalLink className="h-3 w-3" /></a>}
              </div>
              <div className="text-neutral-500 mt-1">{p.audience} — {p.why}</div>
              {!p.pack ? (
                <button onClick={() => genPack(p.id)} disabled={packBusy === p.id} data-testid={`pack-btn-${p.id}`}
                  className="mt-2 text-[11px] font-bold px-3 py-1 rounded-full border border-[#007AFF]/40 text-[#007AFF] hover:bg-[#007AFF]/10 disabled:opacity-50">
                  {packBusy === p.id ? "Generating…" : `✨ Generate pack (${p.language})`}
                </button>
              ) : (
                <div className="mt-2 rounded-lg border border-[#27272A] bg-[#121212] p-2.5">
                  <div className="flex gap-3">
                    <img src={p.pack.flier} alt="flier" className="h-20 rounded object-cover" />
                    <div className="flex-1 text-neutral-300 whitespace-pre-wrap max-h-24 overflow-y-auto">{p.pack.caption}</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button onClick={() => copy(p.pack.caption, "Caption")} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-neutral-700 text-neutral-300 hover:text-white" data-testid={`copy-caption-${p.id}`}><Copy className="h-3 w-3" /> Caption</button>
                    <button onClick={() => copy(p.pack.link, "Link")} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-neutral-700 text-neutral-300 hover:text-white" data-testid={`copy-link-${p.id}`}><Copy className="h-3 w-3" /> Tracked link</button>
                    <button onClick={() => genPack(p.id)} disabled={packBusy === p.id} data-testid={`regen-pack-${p.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 font-bold disabled:opacity-50">
                      {packBusy === p.id ? "Updating…" : "↻ New pitch"}
                    </button>
                    <a href={p.pack.qr_poster} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded-full border border-neutral-700 text-neutral-300 hover:text-white">QR poster</a>
                    <button onClick={() => postTo("telegram", p)} disabled={!cfg.telegram || postBusy === p.id + "telegram"} data-testid={`post-tg-${p.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#229ED9]/15 border border-[#229ED9]/40 text-[#229ED9] font-bold disabled:opacity-40"><Send className="h-3 w-3" /> Telegram</button>
                    <button onClick={() => postTo("twitter", p)} disabled={!cfg.twitter || postBusy === p.id + "twitter"} data-testid={`post-x-${p.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-neutral-500/40 text-neutral-200 font-bold disabled:opacity-40">𝕏 Post</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 pt-4 border-t border-[#27272A]">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1.5">
            <Video className="h-3.5 w-3.5" /> Marketing videos ({videos.length})
          </div>
          <label className="text-[11px] font-bold px-3 py-1 rounded-full border border-[#007AFF]/40 text-[#007AFF] hover:bg-[#007AFF]/10 cursor-pointer" data-testid="video-upload-label">
            + Upload video
            <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadVideo} data-testid="video-upload-input" />
          </label>
        </div>
        {videos.length > 0 && (
          <ul className="mt-2 space-y-1.5 text-xs">
            {videos.map((v) => (
              <li key={v.id} className="flex items-center gap-3" data-testid={`video-${v.id}`}>
                <span className="text-neutral-300 flex-1 truncate">{v.filename}</span>
                <span className="text-neutral-600 font-mono">{(v.size / 1048576).toFixed(1)}MB</span>
                <button onClick={async () => {
                  const cap = window.prompt("Caption for this video post:", "Discover VISMARKS — vismarks.com");
                  if (!cap) return;
                  try { await api.post("/marketing/post/telegram", { caption: cap, video_id: v.id }); toast.success("Video posted to Telegram 🎬"); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
                }} disabled={!cfg.telegram} className="text-[#229ED9] disabled:opacity-40 underline" data-testid={`video-post-${v.id}`}>post</button>
                <button onClick={async () => { await api.delete(`/marketing/videos/${v.id}`).catch(() => {}); load(); }} className="text-neutral-600 hover:text-[#FF3B30]"><Trash2 className="h-3 w-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
